<template>
  <div data-test-id="region-switcher">
    <div
      data-label
      class="btn btn--secondary"
      tabindex="0"
      @click="showDialog"
      @keydown.enter="showDialog"
    >
      {{ $t('message.regionSwitcher.selectedLabel', {countryCode: countryCode, currencyCode: currencyCode}) }}
    </div>
    <Dialog
      :visible="visible"
      :heading="$t('message.regionSwitcher.heading')"
      has-cancel-option
      @cancel="hideDialog"
      @confirm="save"
    >
      <p>{{ $t('message.regionSwitcher.instructions') }}</p>
      <div class="mt-4">
        <Dropdown
          data-countries
          class="mt-4"
          :label="$t('message.regionSwitcher.countryLabel')"
          :options="countries"
          :selected="countryCode"
          @changeEvent="updateCountryCode"
        />
        <Dropdown
          data-currencies
          class="mt-4"
          :label="$t('message.regionSwitcher.currencyLabel')"
          :options="currencies"
          :selected="currencyCode"
          @changeEvent="updateCurrencyCode"
        />
        <Dropdown
          data-languages
          class="mt-4"
          :label="$t('message.regionSwitcher.languageLabel')"
          :options="languages"
          :selected="languageCode"
          @changeEvent="updateLanguageCode"
        />
        <div
          v-if="error"
          data-error
          class="p-2 bg-red-50 text-red-500 border-red-500 rounded mt-4 text-center"
        >
          {{ error }}
        </div>
      </div>
      <Loader
        overlay
        :visible="loading"
        :label="loadingMessage"
        class="absolute inset-0 text-blue-500"
      />
    </Dialog>
  </div>
</template>

<script>
import * as messages from '@vuedir/constants/messages'
import * as errors from '@vuedir/constants/errors'
import { localeUpdate } from '@vuedir/api'

import Dialog from '@vuedir/components/Dialog'
import Dropdown from '@vuedir/components/Dropdown'
import Loader from '@vuedir/components/Loader'

/**
 * RegionSwitcher.vue
 */
export default {
  components: {
    Dialog,
    Dropdown,
    Loader,
  },

  props: {
    countries: {
      type: Array,
      required: true,
    },

    currencies: {
      type: Array,
      required: true,
    },

    languages: {
      type: Array,
      required: true,
    },

    selectedCountry: {
      type: String,
      required: true,
    },

    selectedCurrency: {
      type: String,
      required: true,
    },

    selectedLanguage: {
      type: String,
      required: true,
    },
  },

  data () {
    return {
      visible: false,
      loading: false,
      error: null,
      countryCode: null,
      currencyCode: null,
      languageCode: null,
    }
  },

  computed: {
    loadingMessage () {
      return this.$t(messages.LOADING_SAVING_REGION)
    },
  },

  mounted () {
    this.countryCode = this.selectedCountry
    this.currencyCode = this.selectedCurrency
    this.languageCode = this.selectedLanguage
  },

  methods: {
    showDialog () {
      this.visible = true
    },

    hideDialog () {
      if (!this.loading) {
        this.visible = false
      }
    },

    updateCountryCode (index) {
      this.countryCode = this.countries[index].value
      this.currencyCode = this.countries[index].currencyCode
    },

    updateCurrencyCode (index) {
      this.currencyCode = this.currencies[index].value
    },

    updateLanguageCode (index) {
      this.languageCode = this.languages[index].value
    },

    async save () {
      this.loading = true
      this.error = null

      try {
        const response = await localeUpdate({
          'countryCode': this.countryCode,
          'currencyCode': this.currencyCode,
          'languageCode': this.languageCode,
        })

        const { countryCode, currencyCode, languageCode } = response.data.results

        this.countryCode = countryCode
        this.currencyCode = currencyCode
        this.languageCode = languageCode

        if (window.location.search) {
          window.location.assign(window.location.pathname + window.location.search)
        } else {
          window.location.assign(window.location.pathname)
        }
      } catch (error) {
        this.error = this.$t('message.constants.errors.' + errors.ERROR_SAVE_REGION)
        this.loading = false
      }
    },
  },
}
</script>
